import React, { useEffect, useRef } from 'react';
import { Animated, View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';

export type NotificationType = 'submitting' | 'success' | 'error' | 'warning' | 'info';

interface Props {
  visible: boolean;
  message: string;
  type?: NotificationType;
  onDismiss: () => void;
  duration?: number;
}

const TYPE_CONFIG: Record<NotificationType, { bg: string; icon: string | null }> = {
  submitting: { bg: '#f59e0b', icon: null },
  success:    { bg: '#16a34a', icon: 'check-circle-outline' },
  error:      { bg: '#dc2626', icon: 'alert-circle-outline' },
  warning:    { bg: '#d97706', icon: 'alert-outline' },
  info:       { bg: '#2563eb', icon: 'information-outline' },
};

const NotificationBanner: React.FC<Props> = ({
  visible, message, type = 'info', onDismiss, duration = 3000,
}) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(-60)).current;
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const animateOut = (callback?: () => void) => {
    Animated.parallel([
      Animated.timing(opacity,     { toValue: 0,  duration: 200, useNativeDriver: true }),
      Animated.timing(translateY,  { toValue: -60, duration: 220, useNativeDriver: true }),
    ]).start(callback);
  };

  const handleDismiss = () => {
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    animateOut(() => onDismiss());
  };

  useEffect(() => {
    if (dismissTimer.current) clearTimeout(dismissTimer.current);

    if (visible) {
      Animated.parallel([
        Animated.timing(opacity,    { toValue: 1, duration: 220, useNativeDriver: true }),
        Animated.spring(translateY, { toValue: 0, useNativeDriver: true, tension: 120, friction: 10 }),
      ]).start();

      if (type !== 'submitting' && duration > 0) {
        dismissTimer.current = setTimeout(handleDismiss, duration);
      }
    } else {
      animateOut();
    }

    return () => { if (dismissTimer.current) clearTimeout(dismissTimer.current); };
  }, [visible, type]);

  const config = TYPE_CONFIG[type];

  return (
    <Animated.View
      pointerEvents={visible ? 'box-none' : 'none'}
      style={[styles.wrapper, { opacity, transform: [{ translateY }] }]}
    >
      <View style={[styles.banner, { backgroundColor: config.bg }]}>
        <View style={styles.content}>
          {type === 'submitting' ? (
            <ActivityIndicator size="small" color="#fff" style={styles.icon} />
          ) : (
            config.icon && (
              <MaterialCommunityIcons name={config.icon as any} size={20} color="#fff" style={styles.icon} />
            )
          )}
          <Text style={styles.message} numberOfLines={2}>{message}</Text>
        </View>
        {type !== 'submitting' && (
          <TouchableOpacity onPress={handleDismiss} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <MaterialCommunityIcons name="close" size={18} color="#fff" />
          </TouchableOpacity>
        )}
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: 'fixed' as any,
    top: 24,
    right: 24,
    width: 360,
    zIndex: 999999,
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    marginRight: 10,
  },
  message: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: '#fff',
  },
});

export default NotificationBanner;
